import cookie from 'cookie';
import Cookies from 'js-cookie';
import merge from 'deepmerge';
import { createTheme, Theme, adaptV4Theme } from '@mui/material';

import WhiteLabelTheme from './whitelabel/theme';
import WhiteLabelDefaultTranslations from './whitelabel/translations.en.json';
import { DEFAULT_LANGUAGE, DOMAIN_COMPANY_MAP, SSO_CDH_COMPANY_KEY } from '../../utils/Constants/Constants';
import { TypographyVariants } from '../../types/TypographyTypes';
import { Colours, FontFaceDetails, GlobalSizes, GlobalStyles } from '../../types/ThemeConfigTypes';

export interface Header {
  background?: string
}

export interface Portal {
  assets?: string,
  logo?: string,
  minimisedlogo?: string,
  logoBGSize?: string,
  header?: Header
}

declare module '@mui/material/styles' {
  interface ThemeOptions {
    portal?: Portal;
    customTypography: TypographyVariants;
    colours?: Colours;
    fontFace?: FontFaceDetails[];
    globalStyles?: GlobalStyles;
    globalSizes?: GlobalSizes;
  }

  interface Theme {
    portal: Portal;
    customTypography: TypographyVariants;
    colours: Colours;
    fontFace: FontFaceDetails[];
    globalStyles: GlobalStyles;
    globalSizes: GlobalSizes;
  }
}

export interface APTheme extends Theme { }

const getEnvironment = (hostname: string): keyof typeof DOMAIN_COMPANY_MAP => {
  if (hostname.includes('.sit.')) return 'SIT';
  if (hostname.includes('.uat.')) return 'UAT';
  return 'PROD';
};

const setCdhCompanyCookie = (company: string) => {
  Cookies.set('cdhCompany', company, { path: '/', sameSite: 'none', secure: true });
};

export const getTenant = () => new Promise<string>(resolve => {
  const cookies = cookie.parse(document.cookie);
  const ssoReferringCompany = cookies[SSO_CDH_COMPANY_KEY];
  let referringCompany: string = ssoReferringCompany ?? cookies['cdhCompany'];

  // const ssoReferringCompany: string = cookie.parse(document.cookie)[SSO_CDH_COMPANY_KEY];
  // const referringCompany: string = ssoReferringCompany ?? cookie.parse(document.cookie).cdhCompany;

  if (!referringCompany || referringCompany === 'undefined') {
    Cookies.remove('cdhCompany', { path: '/', sameSite: 'none', secure: true });
    const hostname = window.location.hostname;
    const env = getEnvironment(hostname);
    const envMap = DOMAIN_COMPANY_MAP[env];

    referringCompany = envMap[hostname] ?? 'currenciesdirect';

    if (referringCompany) {
      setCdhCompanyCookie(referringCompany);
    }
  }
  console.log('cdhCompany in tenant setup', cookie.parse(document.cookie).cdhCompany) //temporary
  resolve(referringCompany);
});

export const getTranslations = (language: string | null = DEFAULT_LANGUAGE, legalEntity: string | null = null) =>
  new Promise((resolve) => {
    import(`./whitelabel/translations.${language}.json`).then(WhiteLabelLanguageTranslations => {
      getTenant().then(tenant => {
        if (tenant) {
          import(`./${tenant}/translations.en.json`).then(TenantDefaultTranslations => {
            import(`./${tenant}/translations.${language}.json`).then(TenantLanguageTranslations => {
              import(`./${tenant}/translations.${language}.${legalEntity}.json`).then(LegalEntityTranslations => {
                resolve({
                  ...WhiteLabelDefaultTranslations,
                  ...TenantDefaultTranslations,
                  ...WhiteLabelLanguageTranslations,
                  ...TenantLanguageTranslations,
                  ...LegalEntityTranslations
                });
              }).catch(() => {
                resolve({
                  ...WhiteLabelDefaultTranslations,
                  ...TenantDefaultTranslations,
                  ...WhiteLabelLanguageTranslations,
                  ...TenantLanguageTranslations
                });
              });
            }).catch(() => {
              resolve({
                ...WhiteLabelDefaultTranslations,
                ...TenantDefaultTranslations,
                ...WhiteLabelLanguageTranslations
              });
            });
          }).catch(() => {
            resolve({
              ...WhiteLabelDefaultTranslations,
              ...WhiteLabelLanguageTranslations
            });
          });
        } else {
          resolve({
            ...WhiteLabelDefaultTranslations,
            ...WhiteLabelLanguageTranslations
          });
        }
      });
    }).catch(() => {
      resolve(WhiteLabelDefaultTranslations);
    });
  });

export const getTenantSettings = () => new Promise(resolve => {
  let settings: any = {};

  getTenant().then(tenant => {
    if (tenant) {
      import(`./${tenant}/config`).then((tenantSettings) => {
        resolve(tenantSettings);
      }).catch(() => {
        resolve(settings);
      });
    } else {
      resolve(settings);
    }
  });
});

export const getTenantConfig = () => new Promise(resolve => {
  const WhiteLabelAssigned: any = WhiteLabelTheme;

  getTenantSettings().then(settings => {
    getTenant().then(tenant => {
      if (tenant) {
        import(`./${tenant}/theme`).then(({ default: TenantTheme }) => {
          const merged: any = merge(WhiteLabelAssigned, TenantTheme);
          const theme = createTheme(adaptV4Theme(merged));

          resolve({
            settings,
            tenant,
            theme
          });
        }).catch(() => {
          resolve({
            settings,
            tenant,
            theme: createTheme(adaptV4Theme(WhiteLabelAssigned))
          });
        });
      } else {
        resolve({
          settings,
          tenant,
          theme: createTheme(adaptV4Theme(WhiteLabelAssigned))
        });
      }
    });
  });
});
